import { useState } from "react";
import { YearOfBirthSelect } from "../components/YearOfBirthSelect";
import { ReadOnlyInput } from "../components/ReadOnlyInput";
import { FormField } from "../components/FormField";
import { ProvinceSelect } from "../components/ProvinceSelect";
import { SectionHeader } from "../components/SectionHeader";
import { ShowMoreToggle } from "../components/ShowMoreToggle";
import { MARITAL_STATUS_OPTIONS } from "../types/settings";
import { useProfileLogic } from "./Profile.logic";
import { useAuth } from "../hooks/useAuth";
import { deleteUserAccount } from "../services/userService";
import { auth } from "../lib/firebase";
import { formatErrorForDisplay } from "../types/errors";
import { createLogger } from "../utils/logger";

const profileFormLogger = createLogger({ prefix: "ProfileForm" });

/** Labels for marital status options */
const MARITAL_STATUS_LABELS: Record<string, string> = {
  single: "Single",
  married: "Married",
  "common-law": "Common-law",
  separated: "Separated",
  widowed: "Widowed",
};

/**
 * ProfileForm - Pure form component (no layout)
 * Used by both Profile.mobile (with SettingsPageLayout) and SettingsModal
 *
 * Essential fields: name, email, yearOfBirth, isRetired, province, retirementAge, lifeExpectancy
 * Advanced fields: maritalStatus, spouseYearOfBirth, cppStartAge, oasStartAge, yearsInCanadaAfterAge18
 */
export function ProfileForm() {
  const {
    formData,
    handleChange,
    handleBlur,
    handleSelectChange,
    showAdvanced,
    toggleAdvanced,
    getError,
    loading,
  } = useProfileLogic();

  if (loading) {
    return null;
  }

  return (
    <form className="profile-form-container" id="profile-form">
      {/* ── Personal Details ── */}
      <SectionHeader title="Personal Details" sectionId="personal-details" />

      {/* Name (read-only, set from sign-in provider) */}
      <ReadOnlyInput
        label="Name"
        name="name"
        value={formData.name}
        helpText="Name is set from your sign-in provider"
        suppressPasswordManagers
      />

      {/* Email (read-only) */}
      <ReadOnlyInput
        label="Email"
        name="email"
        value={formData.email ?? ""}
        type="email"
        helpText="Email cannot be changed"
      />

      {/* Year of Birth, Retirement Status, Province */}
      <div className="form-grid">
        <FormField label="What year were you born?" required>
          <YearOfBirthSelect
            id="yearOfBirth"
            value={formData.yearOfBirth ?? null}
            onChange={(year) => handleChange("yearOfBirth", year)}
            onBlur={() => handleBlur("yearOfBirth", formData.yearOfBirth)}
            error={getError("yearOfBirth")}
          />
        </FormField>

        <FormField label="Are you retired?" htmlFor="isRetired" required>
          <select
            id="isRetired"
            name="isRetired"
            value={
              formData.isRetired === null
                ? "select"
                : formData.isRetired
                  ? "yes"
                  : "no"
            }
            onChange={(e) => {
              const v = e.target.value;
              handleSelectChange(
                "isRetired",
                v === "select" ? null : v === "yes"
              );
            }}
            onBlur={() => handleBlur("isRetired", formData.isRetired)}
            className={
              getError("isRetired") ? "form-select-error" : "form-select"
            }
          >
            <option value="select">Please select</option>
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
          {getError("isRetired") && (
            <p className="form-error-message" role="alert">
              {getError("isRetired")}
            </p>
          )}
        </FormField>
      </div>

      {/* Province selector */}
      <div className="form-grid">
        <ProvinceSelect
          value={formData.province ?? ""}
          onChange={(value) =>
            handleSelectChange("province", value === "" ? undefined : value)
          }
        />
      </div>

      {/* ── Planning Timeline ── */}
      <SectionHeader title="Planning Timeline" sectionId="planning-timeline" />

      <div className="form-grid">
        <FormField
          label="Retirement Age"
          htmlFor="retirementAge"
          helpText="When you plan to retire"
        >
          <input
            id="retirementAge"
            name="retirementAge"
            type="number"
            min={50}
            max={80}
            value={formData.retirementAge ?? ""}
            onChange={(e) =>
              handleChange(
                "retirementAge",
                e.target.value ? Number(e.target.value) : undefined
              )
            }
            onBlur={() => handleBlur("retirementAge", formData.retirementAge)}
            className={
              getError("retirementAge") ? "form-input-error" : "form-input"
            }
            placeholder="65"
          />
          {getError("retirementAge") && (
            <p className="form-error-message" role="alert">
              {getError("retirementAge")}
            </p>
          )}
        </FormField>

        <FormField
          label="Life Expectancy"
          htmlFor="lifeExpectancy"
          helpText="How long to plan for"
        >
          <input
            id="lifeExpectancy"
            name="lifeExpectancy"
            type="number"
            min={60}
            max={110}
            value={formData.lifeExpectancy ?? ""}
            onChange={(e) =>
              handleChange(
                "lifeExpectancy",
                e.target.value ? Number(e.target.value) : undefined
              )
            }
            onBlur={() => handleBlur("lifeExpectancy", formData.lifeExpectancy)}
            className={
              getError("lifeExpectancy") ? "form-input-error" : "form-input"
            }
            placeholder="90"
          />
          {getError("lifeExpectancy") && (
            <p className="form-error-message" role="alert">
              {getError("lifeExpectancy")}
            </p>
          )}
        </FormField>
      </div>

      {/* ── Advanced Fields ── */}
      <ShowMoreToggle expanded={showAdvanced} onToggle={toggleAdvanced}>
        <SectionHeader title="Spousal & Household" sectionId="spousal" />

        <div className="form-grid">
          <FormField label="Marital Status" htmlFor="maritalStatus">
            <select
              id="maritalStatus"
              name="maritalStatus"
              value={formData.maritalStatus ?? "select"}
              onChange={(e) => {
                const v = e.target.value;
                handleSelectChange(
                  "maritalStatus",
                  v === "select" ? undefined : v
                );
              }}
              className="form-select"
            >
              <option value="select">Select...</option>
              {MARITAL_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {MARITAL_STATUS_LABELS[status] ?? status}
                </option>
              ))}
            </select>
          </FormField>

          <FormField
            label="Spouse Year of Birth"
            htmlFor="spouseYearOfBirth"
            helpText="Required if married or common-law"
          >
            <input
              id="spouseYearOfBirth"
              name="spouseYearOfBirth"
              type="number"
              min={1930}
              max={new Date().getFullYear()}
              value={formData.spouseYearOfBirth ?? ""}
              onChange={(e) =>
                handleChange(
                  "spouseYearOfBirth",
                  e.target.value ? Number(e.target.value) : undefined
                )
              }
              onBlur={() =>
                handleBlur(
                  "spouseYearOfBirth",
                  formData.spouseYearOfBirth ?? undefined
                )
              }
              className="form-input"
              placeholder="1985"
            />
          </FormField>
        </div>

        <SectionHeader
          title="Government Benefits"
          sectionId="government-benefits"
        />

        <div className="form-grid">
          <FormField
            label="CPP Start Age"
            htmlFor="cppStartAge"
            helpText="Range: 60–70"
          >
            <input
              id="cppStartAge"
              name="cppStartAge"
              type="number"
              min={60}
              max={70}
              value={formData.cppStartAge ?? ""}
              onChange={(e) =>
                handleChange(
                  "cppStartAge",
                  e.target.value ? Number(e.target.value) : undefined
                )
              }
              onBlur={() => handleBlur("cppStartAge", formData.cppStartAge)}
              className={
                getError("cppStartAge") ? "form-input-error" : "form-input"
              }
              placeholder="65"
            />
            {getError("cppStartAge") && (
              <p className="form-error-message" role="alert">
                {getError("cppStartAge")}
              </p>
            )}
          </FormField>

          <FormField
            label="OAS Start Age"
            htmlFor="oasStartAge"
            helpText="Range: 65–70"
          >
            <input
              id="oasStartAge"
              name="oasStartAge"
              type="number"
              min={65}
              max={70}
              value={formData.oasStartAge ?? ""}
              onChange={(e) =>
                handleChange(
                  "oasStartAge",
                  e.target.value ? Number(e.target.value) : undefined
                )
              }
              onBlur={() => handleBlur("oasStartAge", formData.oasStartAge)}
              className={
                getError("oasStartAge") ? "form-input-error" : "form-input"
              }
              placeholder="65"
            />
            {getError("oasStartAge") && (
              <p className="form-error-message" role="alert">
                {getError("oasStartAge")}
              </p>
            )}
          </FormField>

          <FormField
            label="Years in Canada After Age 18"
            htmlFor="yearsInCanadaAfterAge18"
            helpText="For OAS eligibility (full OAS requires 40+ years)"
          >
            <input
              id="yearsInCanadaAfterAge18"
              name="yearsInCanadaAfterAge18"
              type="number"
              min={0}
              max={52}
              value={formData.yearsInCanadaAfterAge18 ?? ""}
              onChange={(e) =>
                handleChange(
                  "yearsInCanadaAfterAge18",
                  e.target.value ? Number(e.target.value) : undefined
                )
              }
              onBlur={() =>
                handleBlur(
                  "yearsInCanadaAfterAge18",
                  formData.yearsInCanadaAfterAge18
                )
              }
              className={
                getError("yearsInCanadaAfterAge18")
                  ? "form-input-error"
                  : "form-input"
              }
              placeholder="40"
            />
            {getError("yearsInCanadaAfterAge18") && (
              <p className="form-error-message" role="alert">
                {getError("yearsInCanadaAfterAge18")}
              </p>
            )}
          </FormField>
        </div>
      </ShowMoreToggle>

      {/* ── Delete Account (post-onboarding only) ── */}
      <DeleteAccountSection />
    </form>
  );
}

function DeleteAccountSection() {
  const { user, onboardingCompleted } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!onboardingCompleted || !user) return null;

  const handleDelete = async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return;

    setDeleting(true);
    setError(null);

    const result = await deleteUserAccount(user.userId, firebaseUser);
    if (!result.ok) {
      profileFormLogger.error("Account deletion failed", {
        error: result.error,
      });
      setError(formatErrorForDisplay(result.error));
      setDeleting(false);
    }
    // On success the auth listener will redirect to sign-in
  };

  return (
    <div className="mt-12 border-t border-slate-700 pt-6">
      <SectionHeader title="Danger Zone" sectionId="danger-zone" />

      {!confirming ? (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="rounded-md border-none bg-red-600 px-5 py-2 font-semibold text-white"
        >
          Delete my account
        </button>
      ) : (
        <div role="alert">
          <p className="mb-2 font-semibold text-red-500">
            This will permanently delete all your data. This action cannot be
            undone.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="rounded-md border-none bg-red-600 px-5 py-2 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {deleting ? "Deleting…" : "Yes, delete my account"}
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirming(false);
                setError(null);
              }}
              disabled={deleting}
              className="rounded-md border border-slate-600 bg-transparent px-5 py-2 font-semibold text-slate-400"
            >
              Cancel
            </button>
          </div>
          {error && (
            <p className="mt-2 text-red-500" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
